import { useCallback, useEffect, useState } from 'react'
import { Avatar, Button, Chip, FieldError, Input, Label, Table, TextField } from '@heroui/react'
import { api } from '../lib/api'
import type { Organisation } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { TimezonePicker } from './parts'
import { Toggle, useFlash, useSave } from './sectionKit'
import { InnerPage, ListCard, Notice, PageHead, useOpenRecord } from './settingsKit'
import '../styles/orgset.css'
import '../styles/install.css'

/**
 * Organisations on this installation.
 *
 * On-premise this holds one row, and it exists so that the second one does not need
 * us. On the hosted service it is the customer list. Either way it is the only place
 * in the product where the tenant boundary is crossed on purpose, which is why it
 * sits behind its own permission and not behind "administrator".
 *
 * A new organisation is created empty — no accounts, no branches. The alternative
 * would be this screen minting an administrator and printing a password, which is
 * the one thing first-run setup exists to avoid. Its first administrator is created
 * on the people screen, where the rule about not creating somebody as senior as
 * yourself still applies.
 */

type Editing = { org: Organisation; isNew: boolean }

export function Organisations({
  currentTenantId,
  onChanged,
}: {
  currentTenantId: string
  onChanged: () => void
}) {
  const { say } = useStaffText()
  const [organisations, setOrganisations] = useState<Organisation[] | null>(null)
  const [find, setFind] = useState('')
  const [loadError, setLoadError] = useState('')
  // The licence's organisation limit, when this person may read the licence. It
  // only greys out Add: the server refuses an organisation over the limit on its
  // own, so a licence that cannot be read leaves the button to the server.
  const [limit, setLimit] = useState(0)
  const { flash, toast } = useFlash()

  const load = useCallback(async () => {
    const listed = await api.allOrganisations()
    setOrganisations(listed.organisations)
    onChanged()
  }, [onChanged])
  const { busy, error, setError, save } = useSave(load, flash)

  useEffect(() => {
    api
      .allOrganisations()
      .then((listed) => setOrganisations(listed.organisations))
      .catch(() => setLoadError('The organisations could not be loaded.'))
    api
      .licence()
      .then((status) => setLimit(status.counted?.find((c) => c.name === 'tenants')?.allowed ?? 0))
      .catch(() => setLimit(0))
  }, [])

  const all = organisations ?? []
  const off = all.filter((o) => !o.active).length
  const full = limit > 0 && all.length >= limit

  // The organisation open on its own page (#organisations/<id>, #organisations/new).
  const rec = useOpenRecord(all, (o) => o.id, organisations !== null)
  const open = (o?: Organisation) => {
    setError('')
    rec.open(o)
  }
  const editing: Editing | false | undefined = rec.isNew
    ? organisations !== null && {
        isNew: true,
        org: { id: '', name: '', slug: '', hostname: '', timezone: 'Asia/Baku', active: true, createdAt: '' },
      }
    : rec.row && { org: rec.row, isNew: false }

  const q = find.trim().toLocaleLowerCase()
  const shown = all.filter(
    (o) => !q || [o.name, o.slug, o.hostname ?? ''].some((x) => x.toLocaleLowerCase().includes(q)),
  )

  if (editing) {
    return (
      <>
        {toast}
        <OrgDrawer
          key={rec.code}
          editing={editing}
          others={all.filter((o) => o.id !== editing.org.id)}
          mine={!editing.isNew && editing.org.id === currentTenantId}
          busy={busy}
          error={error}
          onClose={rec.close}
          onSave={async (draft) => {
            const done = editing.isNew
              ? await save(
                  () =>
                    api.createOrganisation({
                      name: draft.name,
                      ...(draft.slug ? { slug: draft.slug } : {}),
                      ...(draft.hostname ? { hostname: draft.hostname } : {}),
                      timezone: draft.timezone,
                    }),
                  `Created ${draft.name}`,
                )
              : await save(
                  () =>
                    api.updateOrganisation(editing.org.id, {
                      name: draft.name,
                      slug: draft.slug,
                      hostname: draft.hostname,
                      timezone: draft.timezone,
                      active: draft.active,
                    }),
                  `Saved ${draft.name}`,
                )
            if (done) rec.close()
          }}
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.organisations')}
        line={say('settings.orgs.line')}
        actions={
          <Button variant="primary" isDisabled={full || organisations === null} onPress={() => open()}>
            <Icon name="plus" size={16} /> Add organisation
          </Button>
        }
      />
      {toast}
      {loadError && <Notice>{loadError}</Notice>}
      {full && <Notice tone="accent">Licence limit reached. Ask for a larger licence.</Notice>}

      <ListCard
        title={say('admin.section.organisations')}
        count={
          organisations === null ? undefined : (
            <>
              {all.length}
              {limit > 0 && <> of {limit}</>} {(limit > 0 ? limit : all.length) === 1 ? 'organisation' : 'organisations'}
              {off > 0 && <>, {off} off</>}
            </>
          )
        }
        search={find}
        onSearch={all.length > 1 ? setFind : undefined}
        searchLabel={say('settings.orgs.find')}
      >
        {all.length > 0 && (
          <Table variant="secondary" className="org-table">
            <Table.ScrollContainer>
              <Table.Content aria-label={say('admin.section.organisations')} onRowAction={(key) => open(all.find((o) => o.id === key))}>
                <Table.Header>
                  <Table.Column isRowHeader>{say('settings.orgs.col.org')}</Table.Column>
                  <Table.Column>{say('settings.orgs.col.zone')}</Table.Column>
                  <Table.Column>{say('settings.orgs.col.signin')}</Table.Column>
                  <Table.Column className="org-table__act">
                    <span className="sr">{say('settings.orgs.open')}</span>
                  </Table.Column>
                </Table.Header>
                <Table.Body renderEmptyState={() => <p className="org-table__none">{say('admin.findNone', { q: find.trim() })}</p>}>
                  {shown.map((o) => (
                    <Table.Row key={o.id} id={o.id} className={o.active ? undefined : 'org-table__off'}>
                      <Table.Cell>
                        <span className="org-table__who">
                          <Avatar size="sm" color={o.active ? 'accent' : 'default'} variant="soft">
                            <Avatar.Fallback>{o.name.charAt(0).toUpperCase()}</Avatar.Fallback>
                          </Avatar>
                          <span className="org-table__names">
                            <b>
                              {o.name}
                              {o.id === currentTenantId && (
                                <Chip size="sm" variant="soft" color="accent">
                                  {say('settings.orgs.yours')}
                                </Chip>
                              )}
                            </b>
                            <small>
                              <strong className="install-slug">{o.slug}</strong>
                              {o.hostname ? <>, {o.hostname}</> : null}
                            </small>
                          </span>
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="set-muted">{o.timezone}</span>
                      </Table.Cell>
                      <Table.Cell>
                        {o.active ? (
                          <Chip size="sm" variant="soft" color="success">
                            {say('settings.orgs.on')}
                          </Chip>
                        ) : (
                          <Chip size="sm" variant="secondary">
                            {say('settings.orgs.off')}
                          </Chip>
                        )}
                      </Table.Cell>
                      <Table.Cell className="org-table__act">
                        <Button variant="tertiary" size="sm" aria-label={`${say('settings.orgs.open')} ${o.name}`} onPress={() => open(o)}>
                          {say('settings.orgs.open')}
                          <Icon name="right" size={14} />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        )}
      </ListCard>
    </>
  )
}

/** The server's slugify (internal/api/slug.go), so the short name offered while
 *  typing a new name is the one the server would have made from it. */
const FOLDS: Record<string, string> = {
  ə: 'e', ğ: 'g', ı: 'i', ş: 's', ç: 'c', ö: 'o', ü: 'u',
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k',
  л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h',
  ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ы: 'y', э: 'e', ю: 'yu', я: 'ya',
}
function slugOf(name: string): string {
  let out = ''
  let lastDash = true
  for (const ch of name.toLowerCase()) {
    if (/[a-z0-9]/.test(ch)) {
      out += ch
      lastDash = false
    } else if (FOLDS[ch]) {
      out += FOLDS[ch]
      lastDash = false
    } else if (!lastDash) {
      out += '-'
      lastDash = true
    }
  }
  return out.replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '')
}
const SLUG_SHAPE = /^[a-z0-9]+(-[a-z0-9]+)*$/

function OrgDrawer({
  editing,
  others,
  mine,
  busy,
  error,
  onClose,
  onSave,
}: {
  editing: Editing
  others: Organisation[]
  mine: boolean
  busy: boolean
  error: string
  onClose: () => void
  onSave: (draft: Organisation) => void
}) {
  const { say } = useStaffText()
  const { org, isNew } = editing
  const [draft, setDraft] = useState<Organisation>({ ...org, hostname: org.hostname ?? '' })
  const [slugTouched, setSlugTouched] = useState(!isNew)

  const name = draft.name.trim()
  const slug = draft.slug.trim()
  const host = (draft.hostname ?? '').trim().toLowerCase()
  const slugBad = slug !== '' && (!SLUG_SHAPE.test(slug) || slug.length > 40)
  const slugTaken = others.find((o) => o.slug === slug)
  const hostTaken = host !== '' ? others.find((o) => (o.hostname ?? '').toLowerCase() === host) : undefined
  const slugChanged = !isNew && slug !== org.slug
  // The server reads an empty hostname as "leave it as it is", so a cleared box
  // would save as if nothing had been typed. Said here, not discovered later.
  const hostCleared = !isNew && !!org.hostname && host === ''
  const valid = name !== '' && (isNew || slug !== '') && !slugBad && !slugTaken && !hostTaken

  const setName = (next: string) =>
    setDraft((d) => ({ ...d, name: next, slug: slugTouched ? d.slug : slugOf(next) }))

  return (
    <InnerPage
      back={say('admin.section.organisations')}
      title={isNew ? 'New organisation' : org.name}
      mark={
        <Avatar color="accent" variant="soft" aria-hidden="true">
          <Avatar.Fallback>{draft.name.charAt(0) || '?'}</Avatar.Fallback>
        </Avatar>
      }
      onClose={onClose}
      valid={valid}
      busy={busy}
      submitLabel={isNew ? 'Create' : 'Save'}
      onSubmit={() => onSave({ ...draft, name, slug, hostname: host })}
      extra={isNew ? 'Created empty.' : undefined}
    >
      {error && <Notice>{error}</Notice>}

      <TextField value={draft.name} onChange={setName}>
        <Label>Name</Label>
        <Input placeholder="Kapital Bank Leasing" />
      </TextField>

      <TextField
        value={draft.slug}
        isInvalid={slugBad || !!slugTaken}
        onChange={(next) => {
          setSlugTouched(true)
          setDraft({ ...draft, slug: next.toLowerCase() })
        }}
      >
        <Label title="Typed at sign-in.">Short name</Label>
        <Input placeholder="kb-leasing" />
        <FieldError>
          {slugBad ? (
            'Use only a–z, 0–9 and hyphens, up to 40.'
          ) : (
            <>
              {slugTaken?.name} already uses {slug}.
            </>
          )}
        </FieldError>
      </TextField>
      {slugChanged && !slugBad && !slugTaken && <p className="os-warn">People typing {org.slug} will be refused. Tell them first.</p>}

      <TextField value={draft.hostname ?? ''} isInvalid={!!hostTaken} onChange={(hostname) => setDraft({ ...draft, hostname })}>
        <Label title="Optional. Skips the organisation question.">Web address</Label>
        <Input placeholder="navbe.kbleasing.az" />
        <FieldError>
          {hostTaken?.name} already uses {host}.
        </FieldError>
      </TextField>
      {hostCleared && <p className="os-warn">An address cannot be removed here.</p>}

      <div className="field">
        <span title="Branches can override it.">Time zone</span>
        <TimezonePicker value={draft.timezone} onChange={(timezone) => setDraft({ ...draft, timezone })} />
      </div>

      {!isNew && (
        <fieldset className="field">
          <legend>Sign-in</legend>
          <Toggle
            title="Can sign in"
            status={mine ? 'Yours: switching off would lock you out.' : undefined}
            hint={
              mine
                ? undefined
                : draft.active
                  ? 'Off stops everyone signing in. Nothing is deleted.'
                  : 'Nobody can sign in. Nothing was deleted.'
            }
            checked={draft.active}
            disabled={mine}
            onChange={(active) => setDraft({ ...draft, active })}
          />
        </fieldset>
      )}

      {isNew && <Notice tone="accent">Next: add its first administrator in Users.</Notice>}
    </InnerPage>
  )
}
